import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IOption,
  ITeam,
} from "../../../../../../../AdminPanelPage/interfaces/team.interface";
import Select from "react-select";
import { selectStyles } from "../../../../../../../AdminPanelPage/components/ManageUserTab/types/SelectStyles";
import {
  FiChevronDown,
  FiChevronUp,
  FiSend,
  FiX,
} from "react-icons/fi";
import { useQuery } from "@tanstack/react-query";
import { fetchPromptLibrary } from "../../../../../../../AdminPanelPage/components/PromptLibrary/services/fetchPromptLibrary.service";
import { fetchTeamList } from "../../../../../../../AdminPanelPage/services/fetchTeamList.service";
import { IPrompt } from "../../../../../../../AdminPanelPage/components/PromptLibrary/interfaces/prompt.interface";
import { useNavigate } from "react-router-dom";

interface IPromptBlockProps {
  isSubmitted: boolean;
  isPanelCollapsed: boolean;
  setIsPanelCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit: (prompt: string) => void;
}

const PromptBlock: React.FC<IPromptBlockProps> = ({
  setIsPanelCollapsed,
  handleSubmit,
}) => {
  const { t } = useTranslation();
  const darkMode = localStorage.getItem("darkMode") === "true";
  const navigate = useNavigate();

  const [selectedTeam, setSelectedTeam] = useState<IOption>({
    value: "",
    label: "All Prompts",
  });
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [expandableMap, setExpandableMap] = useState<Record<number, boolean>>(
    {},
  );

  const previewRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const { data: promptList = [] } = useQuery({
    queryKey: ["promptList"],
    queryFn: () => fetchPromptLibrary(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: teamList } = useQuery({
    queryKey: ["teamList"],
    queryFn: () => fetchTeamList(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const teamOptions: IOption[] = useMemo(
    () => [
      { value: "", label: "All Prompts" },
      { value: "0", label: "My Prompts" },
      ...(teamList
        ? teamList.map((team: ITeam) => ({
            value: team.id.toString(),
            label: team.name,
          }))
        : []),
    ],
    [teamList],
  );

  useEffect(() => {
    if (
      teamOptions.length > 0 &&
      !teamOptions.find((option) => option.value === selectedTeam.value)
    ) {
      setSelectedTeam(teamOptions[0]);
    }
  }, [teamOptions, selectedTeam.value]);

  const filteredPrompts = useMemo(
    () =>
      promptList.filter((prompt: IPrompt) => {
        if (selectedTeam.value === "") return true;
        if (selectedTeam.value === "0") return prompt.team === null;

        return prompt?.team
          ? prompt.team.id.toString() === selectedTeam.value
          : false;
      }),
    [promptList, selectedTeam.value],
  );

  useEffect(() => {
    const measure = () => {
      const nextMap: Record<number, boolean> = {};

      filteredPrompts.forEach((_: IPrompt, index: number) => {
        const el = previewRefs.current[index];
        if (!el) {
          nextMap[index] = false;
          return;
        }

        nextMap[index] = el.scrollHeight > el.clientHeight + 1;
      });

      setExpandableMap(nextMap);
    };

    const raf = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [filteredPrompts, expandedIndex]);

  const toggleExpand = (index: number) => {
    if (!expandableMap[index]) return;
    setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  return (
    <div className="h-full min-h-0 w-full">
      <div className="h-full min-h-0 w-full overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800/80 shadow-sm flex flex-col">
        <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-3 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {t("chatAI.promptLibraryTitle", {
                  defaultValue: "Prompt library",
                })}
              </h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {t("chatAI.promptLibrarySubtitle", {
                  defaultValue: "Pick a saved prompt or manage your collection",
                })}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate("/admin_panel/prompt_library")}
                className="rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                {t("promptLibrary.promptLibraryLink")}
              </button>

              <button
                onClick={() => setIsPanelCollapsed(true)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                aria-label={t("chatAI.closePromptLibrary", {
                  defaultValue: "Close prompt library",
                })}
              >
                <FiX size={16} />
              </button>
            </div>
          </div>

          <div className="mt-3 max-w-[320px]">
            <Select
              value={selectedTeam}
              onChange={(option) => {
                if (option) setSelectedTeam(option);
              }}
              options={teamOptions}
              classNamePrefix="react-select"
              styles={{
                ...selectStyles(darkMode),
                container: (base) => ({
                  ...base,
                  width: "100%",
                }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: darkMode ? "#0f172a" : "#ffffff",
                  borderColor: state.isFocused
                    ? "#86BC25"
                    : darkMode
                      ? "#475569"
                      : "#cbd5e1",
                  minHeight: 40,
                  boxShadow: state.isFocused
                    ? "0 0 0 2px rgba(134,188,37,0.18)"
                    : "none",
                  borderWidth: "1px",
                  borderRadius: "0.75rem",
                  transition: "box-shadow 0.2s, border-color 0.2s",
                  ":hover": {
                    borderColor: state.isFocused
                      ? "#86BC25"
                      : darkMode
                        ? "#64748b"
                        : "#94a3b8",
                  },
                }),
                valueContainer: (base) => ({
                  ...base,
                  paddingLeft: 12,
                  paddingRight: 8,
                }),
                placeholder: (base) => ({
                  ...base,
                  color: darkMode ? "#94a3b8" : "#64748b",
                  fontSize: "14px",
                }),
                singleValue: (base) => ({
                  ...base,
                  color: darkMode ? "#e2e8f0" : "#0f172a",
                  fontSize: "14px",
                }),
                menu: (base) => ({
                  ...base,
                  borderRadius: "0.75rem",
                  overflow: "hidden",
                  zIndex: 20,
                }),
                option: (base, { isFocused, isSelected }) => ({
                  ...base,
                  backgroundColor: isSelected
                    ? "#86BC25"
                    : isFocused
                      ? darkMode
                        ? "#334155"
                        : "#f1f5f9"
                      : darkMode
                        ? "#1f2937"
                        : "#ffffff",
                  color: isSelected || darkMode ? "#ffffff" : "#0f172a",
                  fontSize: "14px",
                }),
              }}
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 w-full overflow-y-auto px-3 py-3">
          {filteredPrompts.length === 0 ? (
            <div className="flex h-full min-h-[180px] w-full items-center justify-center rounded-xl border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t("noData")}
              </p>
            </div>
          ) : (
            <ul className="w-full space-y-2">
              {filteredPrompts.map((prompt: IPrompt, index: number) => {
                const isExpanded = expandedIndex === index;
                const isExpandable = !!expandableMap[index];

                return (
                  <li key={`prompt-${index}`} className="w-full">
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 px-3 py-3 transition-colors hover:bg-slate-100/70 dark:hover:bg-slate-900/50">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {prompt.name}
                            </span>

                            {prompt.team && (
                              <span className="inline-flex items-center rounded-lg bg-slate-200 px-2 py-1 text-[11px] font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                {prompt.team.name}
                              </span>
                            )}
                          </div>

                          <div
                            ref={(el) => {
                              previewRefs.current[index] = el;
                            }}
                            className="mt-2 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300"
                            style={
                              isExpanded
                                ? undefined
                                : {
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }
                            }
                          >
                            {prompt.text}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          {isExpandable && (
                            <button
                              onClick={() => toggleExpand(index)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-white dark:text-slate-300 dark:hover:bg-slate-800"
                              aria-label={
                                isExpanded
                                  ? t("chatAI.collapsePrompt", {
                                      defaultValue: "Collapse prompt",
                                    })
                                  : t("chatAI.expandPrompt", {
                                      defaultValue: "Expand prompt",
                                    })
                              }
                            >
                              {isExpanded ? (
                                <FiChevronUp size={18} />
                              ) : (
                                <FiChevronDown size={18} />
                              )}
                            </button>
                          )}

                          <button
                            onClick={() => handleSubmit(prompt.text)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-del-green/10 text-del-green hover:bg-del-green/20 dark:bg-del-green/20"
                            aria-label={t("chatAI.sendPrompt")}
                          >
                            <FiSend size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export { PromptBlock };